import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import {
  CategoricalFilterCriterion,
  CategoricalOption,
  ColumnFilterConfig,
  ColumnFilterCriterion,
  DateRangeFilterCriterion,
  NumberRangeFilterCriterion,
  NumberTarget,
  TextFilterCriterion,
  isCriterionActive,
} from '../table-filter.models';

/**
 *
 */
@Component({
  selector: 'app-column-filter-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './column-filter-editor.component.html',
  styleUrls: ['./column-filter-editor.component.scss'],
})
export class ColumnFilterEditorComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);

  @Input({ required: true }) config!: ColumnFilterConfig;
  @Input() criterion?: ColumnFilterCriterion;

  @Output() criterionChange = new EventEmitter<ColumnFilterCriterion | undefined>();
  @Output() clear = new EventEmitter<void>();

  textControl = new FormControl('', { nonNullable: true });
  categoricalControl = new FormControl<string[]>([], { nonNullable: true });
  startDateControl = new FormControl('', { nonNullable: true });
  endDateControl = new FormControl('', { nonNullable: true });
  targetControl = new FormControl<NumberTarget>('count', { nonNullable: true });
  minControl = new FormControl<number | string | null>(null);
  maxControl = new FormControl<number | string | null>(null);

  focusFirstInput(): void {
    const root = this.elementRef.nativeElement as HTMLElement;
    const focusable = root.querySelector<HTMLElement>('input:not([disabled]), [tabindex="0"]:not([disabled])');
    focusable?.focus();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' || event.key === 'Tab') {
      return;
    }
    event.stopPropagation();
  }

  private isSyncingFromInput = false;

  get normalizedOptions(): CategoricalOption[] {
    if (!this.config?.options) return [];
    return this.config.options.map(opt => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  }

  get isActive(): boolean {
    return isCriterionActive(this.criterion);
  }

  ngOnInit(): void {
    if (this.config.defaultTarget) {
      this.targetControl.setValue(this.config.defaultTarget, { emitEvent: false });
    }

    this.syncControlsFromCriterion();

    this.textControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.categoricalControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.startDateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.endDateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.targetControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.minControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());

    this.maxControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emitChanges());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['criterion'] && !changes['criterion'].firstChange) {
      this.syncControlsFromCriterion();
    }
  }

  clearFilter(): void {
    this.isSyncingFromInput = true;
    try {
      this.textControl.setValue('');
      this.categoricalControl.setValue([]);
      this.startDateControl.setValue('');
      this.endDateControl.setValue('');
      this.minControl.setValue(null);
      this.maxControl.setValue(null);
      if (this.config.defaultTarget) {
        this.targetControl.setValue(this.config.defaultTarget);
      }
    } finally {
      this.isSyncingFromInput = false;
    }
    this.criterion = undefined;
    this.criterionChange.emit(undefined);
    this.clear.emit();
  }

  private syncControlsFromCriterion(): void {
    this.isSyncingFromInput = true;
    try {
      if (!this.criterion) {
        this.textControl.setValue('', { emitEvent: false });
        this.categoricalControl.setValue([], { emitEvent: false });
        this.startDateControl.setValue('', { emitEvent: false });
        this.endDateControl.setValue('', { emitEvent: false });
        this.minControl.setValue(null, { emitEvent: false });
        this.maxControl.setValue(null, { emitEvent: false });
        if (this.config?.defaultTarget) {
          this.targetControl.setValue(this.config.defaultTarget, { emitEvent: false });
        }
        return;
      }

      switch (this.criterion.type) {
        case 'text':
          this.textControl.setValue((this.criterion as TextFilterCriterion).query || '', {
            emitEvent: false,
          });
          break;
        case 'categorical':
          this.categoricalControl.setValue([...((this.criterion as CategoricalFilterCriterion).values || [])], {
            emitEvent: false,
          });
          break;
        case 'date-range': {
          const dc = this.criterion as DateRangeFilterCriterion;
          this.startDateControl.setValue(dc.start || '', { emitEvent: false });
          this.endDateControl.setValue(dc.end || '', { emitEvent: false });
          break;
        }
        case 'number-range': {
          const nc = this.criterion as NumberRangeFilterCriterion;
          this.minControl.setValue(nc.min ?? null, { emitEvent: false });
          this.maxControl.setValue(nc.max ?? null, { emitEvent: false });
          if (nc.target) {
            this.targetControl.setValue(nc.target, { emitEvent: false });
          } else if (this.config?.defaultTarget) {
            this.targetControl.setValue(this.config.defaultTarget, { emitEvent: false });
          }
          break;
        }
      }
    } finally {
      this.isSyncingFromInput = false;
    }
  }

  private emitChanges(): void {
    if (this.isSyncingFromInput) return;

    let criterion: ColumnFilterCriterion | undefined;

    switch (this.config.type) {
      case 'text': {
        const query = this.textControl.value.trim();
        criterion = query ? { type: 'text', query } : undefined;
        break;
      }
      case 'categorical': {
        const values = this.categoricalControl.value;
        criterion = values && values.length > 0 ? { type: 'categorical', values } : undefined;
        break;
      }
      case 'date-range': {
        const start = this.startDateControl.value.trim() || null;
        const end = this.endDateControl.value.trim() || null;
        criterion = start || end ? { type: 'date-range', start, end } : undefined;
        break;
      }
      case 'number-range': {
        const minVal = this.minControl.value;
        const maxVal = this.maxControl.value;
        const min = minVal !== null && minVal !== '' && !isNaN(Number(minVal)) ? Number(minVal) : null;
        const max = maxVal !== null && maxVal !== '' && !isNaN(Number(maxVal)) ? Number(maxVal) : null;
        const target = this.targetControl.value;
        criterion = min !== null || max !== null ? { type: 'number-range', min, max, target } : undefined;
        break;
      }
    }

    this.criterion = criterion;
    this.criterionChange.emit(criterion);
  }
}
